import type { ApiDeviationsReport } from './transportExecution.reportDeviations.schema';
import type {
    DeviationsReport,
    ShipmentHistoryAddress,
    ShipmentHistoryEntry,
    ShipmentHistoryParty,
    ShipmentHistoryReportedBy,
    ShipmentHistoryStatusLocation,
} from '../../domain/model/shipment/ShipmentHistory.types';
import type { ApiShipmentHistoryEntry } from './transportExecution.shipmentHistory.schema';

type ApiShipmentHistoryParty = NonNullable<
    | ApiShipmentHistoryEntry['loading']
    | ApiShipmentHistoryEntry['unloading']
    | ApiShipmentHistoryEntry['freight_forwarder']
>;
type ApiShipmentHistoryAddress = NonNullable<ApiShipmentHistoryParty['address']>;

export const toShipmentHistoryEntry = (src: ApiShipmentHistoryEntry): ShipmentHistoryEntry => ({
    type: src.type,
    occurredAt: src.occurred_at,
    reportedBy: toReportedBy(src.reported_by),
    deviationsReport: src.deviations_report && toDeviationsReport(src.deviations_report),
    statusLocation: src.status_location && toStatusLocation(src.status_location),
    documents:
        src.documents?.map(document => ({
            documentId: document.document_id,
            fileName: document.file_name,
        })) ?? [],
    loading: src.loading && toParty(src.loading),
    unloading: src.unloading && toParty(src.unloading),
    freightForwarder: src.freight_forwarder && toParty(src.freight_forwarder),
});

const toReportedBy = (src: ApiShipmentHistoryEntry['reported_by']): ShipmentHistoryReportedBy =>
    src.type === 'INTERNAL'
        ? {
              type: src.type,
              role: src.role,
              duns: src.duns,
              name: src.name,
          }
        : {
              type: src.type,
              supplierCode: src.supplier_code,
              duns: src.duns,
              role: src.role,
          };

const toParty = (src: ApiShipmentHistoryParty): ShipmentHistoryParty => ({
    role: src.role,
    name: src.name,
    address: src.address && toAddress(src.address),
});

const toAddress = (src: ApiShipmentHistoryAddress): ShipmentHistoryAddress => ({
    city: src.city,
    postalCode: src.postal_code,
    countryCode: src.country_code,
});

const toStatusLocation = (
    src: NonNullable<ApiShipmentHistoryEntry['status_location']>
): ShipmentHistoryStatusLocation => ({
    physicalLocation:
        'physical_location' in src
            ? {
                  name: src.physical_location.name,
                  city: src.physical_location.city,
                  countryCode: src.physical_location.country_code,
              }
            : undefined,
    duns: 'duns' in src ? src.duns : undefined,
});

const toDeviationsReport = (src: ApiDeviationsReport): DeviationsReport => {
    return {
        comment: src.comment ?? undefined,
        deviations: src.deviations.map(deviation => ({
            handlingUnitCode: deviation.handling_unit_code,
            actualQuantity: deviation.actual_quantity,
            plannedQuantity: deviation.planned_quantity,
            damagedQuantity: deviation.damaged_quantity,
            description: deviation.description ?? undefined,
            handlingUnitContent: deviation.handling_unit_content?.map(content => ({
                quantity: content.quantity,
                code: content.code,
            })),
        })),
    };
};
